import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { SectionHead } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/Reveal";
import { trackCall } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUILDING_OPTIONS = [
  "Pasture or property fence",
  "Corral or livestock pen",
  "Yard or residential fence",
  "Commercial or ranch project",
  "Something else",
];

const HEARD_OPTIONS = ["Search", "Social", "QR code on the package", "Word of mouth", "Trade show", "Other"];

const empty = {
  name: "",
  phone: "",
  email: "",
  city_state: "",
  building_type: "",
  project_details: "",
  heard_about: "",
  website: "",
};

export const QuoteForm = ({ estimate }) => {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (estimate) {
      setForm((f) => ({
        ...f,
        project_details: f.project_details ? `${f.project_details}\n\n${estimate}` : estimate,
      }));
    }
  }, [estimate]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Enter your name.";
    if (form.phone.replace(/\D/g, "").length < 7) er.phone = "Enter a phone number we can call.";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) er.email = "Enter a valid email address.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailed(false);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, email: form.email.trim() });
      setDone(true);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section" id="quote" data-testid="quote-section">
      <div className="wrap">
        <SectionHead
          num="07"
          eyebrow="Quote"
          title="Request a quote."
          lede="Tell us what you are building and we will call you back with a quote."
        />
        <Reveal>
          <div className="quote-card">
            {done ? (
              <div className="form-success" data-testid="quote-success-message">
                <CheckCircle2 size={40} color="var(--blue)" aria-hidden="true" />
                <h3>Got it.</h3>
                <p>
                  We will call you at the number you gave us. If you need an answer today, call{" "}
                  <a href="tel:+18666526569" onClick={() => trackCall("quote-success")} style={{ color: "var(--blue)", fontWeight: 600 }}>866-652-6569</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="quote-form">
                <div className="quote-grid">
                  <div className="field">
                    <label htmlFor="q-name">Name *</label>
                    <input
                      id="q-name"
                      data-testid="quote-name-input"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={set("name")}
                      aria-invalid={errors.name ? "true" : undefined}
                      aria-describedby={errors.name ? "q-name-err" : undefined}
                    />
                    {errors.name && (
                      <span className="field-err" id="q-name-err" data-testid="quote-name-error">{errors.name}</span>
                    )}
                  </div>
                  <div className="field">
                    <label htmlFor="q-phone">Phone *</label>
                    <input
                      id="q-phone"
                      data-testid="quote-phone-input"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      aria-invalid={errors.phone ? "true" : undefined}
                      aria-describedby={errors.phone ? "q-phone-err" : undefined}
                    />
                    {errors.phone && (
                      <span className="field-err" id="q-phone-err" data-testid="quote-phone-error">{errors.phone}</span>
                    )}
                  </div>
                  <div className="field">
                    <label htmlFor="q-email">Email *</label>
                    <input
                      id="q-email"
                      data-testid="quote-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set("email")}
                      aria-invalid={errors.email ? "true" : undefined}
                      aria-describedby={errors.email ? "q-email-err" : undefined}
                    />
                    {errors.email && (
                      <span className="field-err" id="q-email-err" data-testid="quote-email-error">{errors.email}</span>
                    )}
                  </div>
                  <div className="field">
                    <label htmlFor="q-city">City and state (optional)</label>
                    <input
                      id="q-city"
                      data-testid="quote-city-input"
                      type="text"
                      autoComplete="address-level2"
                      value={form.city_state}
                      onChange={set("city_state")}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="q-building">What are you building?</label>
                    <select id="q-building" data-testid="quote-building-select" value={form.building_type} onChange={set("building_type")}>
                      <option value="">Select one…</option>
                      {BUILDING_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="q-heard">How did you hear about us?</label>
                    <select id="q-heard" data-testid="quote-heard-select" value={form.heard_about} onChange={set("heard_about")}>
                      <option value="">Select one…</option>
                      {HEARD_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field full">
                    <label htmlFor="q-details">Project details</label>
                    <textarea
                      id="q-details"
                      data-testid="quote-details-textarea"
                      rows="5"
                      value={form.project_details}
                      onChange={set("project_details")}
                    />
                  </div>
                  {/* honeypot — hidden from real users */}
                  <div className="hp-field" aria-hidden="true">
                    <label htmlFor="q-website">Website</label>
                    <input
                      id="q-website"
                      type="text"
                      tabIndex="-1"
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </div>
                </div>

                {failed && (
                  <div className="form-fail" role="alert" data-testid="quote-fail-message">
                    That did not go through. Call{" "}
                    <a href="tel:+18666526569" onClick={() => trackCall("quote-fail")} style={{ color: "var(--err)", fontWeight: 600 }}>866-652-6569</a>{" "}
                    and we will take your information over the phone.
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ marginTop: 26 }}
                  disabled={submitting}
                  data-testid="quote-submit-btn"
                >
                  {submitting ? "Sending…" : "Request my quote"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
